/**
 * The Form 1099-NEC worksheet: the vendors this business reports, what each was
 * paid, and the IRS's own form filled for each (issue #49, the operator,
 * 2026-10-09: "I don't but still add that feature so it's ready when I need
 * it").
 *
 * A WORKSHEET, AND IT SAYS SO FIRST, as the 941, 940 and W-2 worksheets do. The
 * figures are GET /payroll/forms/1099-nec's (apps/api/src/services/form-1099-nec.ts);
 * this screen computes nothing.
 *
 * THE TAX ID IS ENTERED HERE AND NEVER SHOWN. PartiesPage's header kept the
 * field off the parties list until "the screen that files the 1099, when that
 * screen exists": this is that screen. What is typed is sent once and
 * encrypted; the API answers only whether one is on file, so the field reads
 * "On file" rather than the number, and the filled form shows its last four.
 *
 * MARKING IS THE OPERATOR'S, AND THE UNMARKED ARE LISTED. Whether a vendor's
 * payments were for services is a judgement no entry shows, so a vendor is
 * reported only once marked, and every vendor paid at least the year's
 * threshold who is not marked is listed beneath, to be decided rather than
 * missed. Each action re-reads the worksheet, so the two lists never disagree.
 *
 * The year is kept in the address, as the W-2 worksheet's is.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type Form1099NecReport } from '../api.js';
import { formatCents } from '../lib/money.js';

type Recipient = Form1099NecReport['recipients'][number];

export function Form1099NecPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam === null ? undefined : Number(yearParam);
  const [report, setReport] = useState<Form1099NecReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reads, setReads] = useState(0);

  useEffect(() => {
    let live = true;
    api
      .getForm1099Nec(year)
      .then((result) => {
        if (!live) return;
        setReport(result.report);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not build the worksheet.');
      });
    return () => {
      live = false;
    };
  }, [year, reads]);

  function choose(value: string) {
    const params = new URLSearchParams(searchParams);
    params.set('year', value);
    setSearchParams(params, { replace: true });
  }

  const shown = year ?? report?.year;
  const thisYear = new Date().getFullYear();
  const choices = shown === undefined ? [] : [...new Set([thisYear, shown, shown - 1, shown - 2])].sort((a, b) => b - a);

  return (
    <section className="form-1099-nec">
      <p>
        <Link to="/payroll">Back to Payroll</Link>
      </p>
      <h1>
        Form 1099-NEC Worksheet{' '}
        <InfoNote label="What Counts as Paid">
          Money that left a bank account on a posted entry naming the vendor, dated by when the bank moved it. A payment
          by card is not counted: the card company reports it on Form 1099-K. A bill not yet paid is not counted, nor a
          payment that was reversed. Money the same party paid in is shown beside box 1a and never taken off it: a deposit
          from a party who is also a customer is not a refund, so a genuine refund is yours to take off on the form. The
          threshold is the year&apos;s own, from the IRS&apos;s Instructions for Forms 1099-MISC and 1099-NEC: $2,000 for
          2026, $600 before.
        </InfoNote>
      </h1>

      {report !== null && shown !== undefined && (
        <div className="row report-period">
          <label>
            Year{' '}
            <select value={shown} onChange={(event) => choose(event.target.value)}>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {error === null && (report === null || report.year !== shown) && <p className="muted">Loading…</p>}
      {error === null && report !== null && report.year === shown && (
        <Worksheet report={report} reread={() => setReads((count) => count + 1)} />
      )}
    </section>
  );
}

function Worksheet({ report, reread }: { report: Form1099NecReport; reread: () => void }) {
  return (
    <>
      <p className="notice">
        A worksheet: the vendors to report on Form 1099-NEC for {report.year} and what each was paid. Nothing here files
        them. Copy B goes to each recipient, and Copy A to the IRS through its online filing (IRIS), by {report.due.date}
        {report.due.date !== report.due.statutory && <>, the first business day after {report.due.statutory}</>}.
      </p>
      <p className="muted">
        {report.thresholdCents === null
          ? `The IRS has not given this application ${report.year}'s threshold, which is adjusted for inflation from 2027, so no vendor is marked as required.`
          : `A vendor paid ${formatCents(report.thresholdCents)} or more in ${report.year} for services must receive one.`}
      </p>

      <section className="settings-section" aria-labelledby="nec-recipients">
        <h2 id="nec-recipients">Vendors You Report</h2>
        {report.recipients.length === 0 ? (
          <p className="muted">No vendor is marked to receive a Form 1099-NEC.</p>
        ) : (
          report.recipients.map((recipient) => <RecipientRow key={recipient.partyId} recipient={recipient} year={report.year} reread={reread} />)
        )}
      </section>

      {report.unmarked.length > 0 && (
        <section className="settings-section" aria-labelledby="nec-unmarked">
          <h2 id="nec-unmarked">Vendors to Decide About</h2>
          <p className="muted">
            Paid at least the threshold in {report.year} and not marked. Mark one if its payments were for services.
          </p>
          <ul className="history-list">
            {report.unmarked.map((vendor) => (
              <UnmarkedRow key={vendor.partyId} vendor={vendor} reread={reread} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function RecipientRow({ recipient, year, reread }: { recipient: Recipient; year: number; reread: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const file = await api.downloadForm1099NecPdf(year, recipient.partyId);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = `form-1099-nec-${year}-${recipient.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not fill the form.');
    } finally {
      setBusy(false);
    }
  }

  async function unmark() {
    setError(null);
    try {
      await api.updateParty(recipient.partyId, { receives1099Nec: false });
      reread();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not unmark the vendor.');
    }
  }

  return (
    <div className="nec-recipient" role="group" aria-label={recipient.name}>
      <h3>{recipient.name}</h3>
      <dl className="record-facts">
        <dt>Box 1a</dt>
        <dd>
          <span className="num">{formatCents(recipient.paidCents)}</span>
          {recipient.required === true && ' (required)'}
          {recipient.required === false && ' (below the threshold)'}
        </dd>
        {recipient.receivedCents > 0 && (
          <>
            <dt>Received From Them</dt>
            <dd>
              <span className="num">{formatCents(recipient.receivedCents)}</span>, not taken off box 1a
            </dd>
          </>
        )}
        <dt>Tax ID</dt>
        <dd>{recipient.hasTaxId ? 'On file' : 'Not entered'}</dd>
        <dt>Address</dt>
        <dd>{recipient.address.length === 0 ? 'Not entered' : recipient.address.join(', ')}</dd>
      </dl>
      <p>
        <button type="button" disabled={busy} onClick={() => void download()}>
          {busy ? 'Filling…' : 'Download Filled Form 1099-NEC'}
        </button>{' '}
        <button type="button" className="link" onClick={() => setEditing((open) => !open)}>
          {editing ? 'Close' : 'Tax ID and Address'}
        </button>{' '}
        <button type="button" className="link" onClick={() => void unmark()}>
          Unmark
        </button>{' '}
        <InfoNote label="What the Filled Form Is">
          The IRS&apos;s own Form 1099-NEC, fetched from irs.gov and filled on Copy B, for the recipient: the payer from
          Business Details in Settings, the recipient&apos;s tax id shown by its last four digits only, and box 1a. Copy A
          is left blank: the IRS says one printed from its website cannot be scanned, so it is filed through IRIS or on
          the official red form. Every field stays editable.
        </InfoNote>
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {editing && (
        <RecipientDetails
          recipient={recipient}
          onSaved={() => {
            setEditing(false);
            reread();
          }}
        />
      )}
    </div>
  );
}

function RecipientDetails({ recipient, onSaved }: { recipient: Recipient; onSaved: () => void }) {
  const [taxId, setTaxId] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      // Only what is typed is sent: a field left blank keeps what is on file, so saving a
      // tax id alone cannot erase an address the form never showed.
      const typed = (value: string) => (value.trim() === '' ? undefined : value.trim());
      await api.updateParty(recipient.partyId, {
        taxId: typed(taxId),
        addressLine1: typed(street),
        city: typed(city),
        region: typed(region),
        postalCode: typed(postalCode),
      });
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="new-account authenticator-form" role="group" aria-label={`Tax ID and address for ${recipient.name}`}>
      <div className="field-row">
        <label className="field field-medium">
          <span>Tax ID</span>
          <input
            value={taxId}
            autoComplete="off"
            placeholder={recipient.hasTaxId ? 'On file' : 'SSN or EIN'}
            onChange={(event) => {
              const value = event.target.value;
              setTaxId(value);
            }}
          />
        </label>
        <label className="field">
          <span>Street Address</span>
          <input
            value={street}
            onChange={(event) => {
              const value = event.target.value;
              setStreet(value);
            }}
          />
        </label>
        <label className="field field-medium">
          <span>City</span>
          <input
            value={city}
            onChange={(event) => {
              const value = event.target.value;
              setCity(value);
            }}
          />
        </label>
        <label className="field field-short">
          <span>State</span>
          <input
            value={region}
            maxLength={2}
            onChange={(event) => {
              const value = event.target.value;
              setRegion(value);
            }}
          />
        </label>
        <label className="field field-medium">
          <span>ZIP Code</span>
          <input
            value={postalCode}
            onChange={(event) => {
              const value = event.target.value;
              setPostalCode(value);
            }}
          />
        </label>
      </div>
      <p className="muted">A field left blank keeps what is on file; what you type replaces it.</p>
      <button type="button" disabled={busy} onClick={() => void save()}>
        {busy ? 'Saving…' : 'Save Tax ID and Address'}
      </button>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </div>
  );
}

function UnmarkedRow({ vendor, reread }: { vendor: Form1099NecReport['unmarked'][number]; reread: () => void }) {
  const [error, setError] = useState<string | null>(null);
  async function mark() {
    setError(null);
    try {
      await api.updateParty(vendor.partyId, { receives1099Nec: true });
      reread();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not mark the vendor.');
    }
  }
  return (
    <li>
      {vendor.name}, paid {formatCents(vendor.paidCents)}{' '}
      <button type="button" className="link" onClick={() => void mark()}>
        Mark to Receive
      </button>
      {error && (
        <span role="alert" className="error">
          {' '}
          {error}
        </span>
      )}
    </li>
  );
}
